/**
 * investorData.js
 * Central data store for all Investor tab features.
 * Structured data for fundraising, IP, risks, pipeline, documents, and alerts.
 */

// ---------------------------------------------------------------------------
// Available reporting periods
// ---------------------------------------------------------------------------
export const AVAILABLE_PERIODS = ['jan-2026', 'dec-2025', 'nov-2025'];

// ---------------------------------------------------------------------------
// Next Raise
// ---------------------------------------------------------------------------
export const NEXT_RAISE = {
  targetAmount: 75000000,
  currency: 'USD',
  expectedTimeline: 'Q3 2027',
  purpose: 'Complete Pivotal Study and obtain PMA',
  postRaiseRunway: '36+ months',
  useOfProceeds: [
    { category: 'Clinical Trials (Pivotal Study)', amount: 30000000, pct: 40 },
    { category: 'Manufacturing Scale-Up', amount: 15000000, pct: 20 },
    { category: 'R&D (Next-Gen Development)', amount: 11250000, pct: 15 },
    { category: 'Regulatory & Quality', amount: 7500000, pct: 10 },
    { category: 'G&A / Operations', amount: 7500000, pct: 10 },
    { category: 'Working Capital', amount: 3750000, pct: 5 },
  ],
};

// ---------------------------------------------------------------------------
// Patent Data
// ---------------------------------------------------------------------------
export const PATENT_DATA = {
  summary: { filed: 52, granted: 9, pending: 43, countries: 10 },
  byProduct: [
    { product: 'P01 - Catheter System', filed: 28, granted: 6, pending: 22 },
    { product: 'P02 - Delivery System', filed: 15, granted: 2, pending: 13 },
    { product: 'P03 - Imaging Module', filed: 9, granted: 1, pending: 8 },
  ],
  byJurisdiction: [
    { country: 'United States', code: 'US', filed: 18, granted: 4, pending: 14 },
    { country: 'European Union', code: 'EU', filed: 12, granted: 2, pending: 10 },
    { country: 'Canada', code: 'CA', filed: 8, granted: 2, pending: 6 },
    { country: 'Japan', code: 'JP', filed: 5, granted: 1, pending: 4 },
    { country: 'China', code: 'CN', filed: 4, granted: 0, pending: 4 },
    { country: 'Australia', code: 'AU', filed: 2, granted: 0, pending: 2 },
    { country: 'South Korea', code: 'KR', filed: 1, granted: 0, pending: 1 },
    { country: 'Brazil', code: 'BR', filed: 1, granted: 0, pending: 1 },
    { country: 'India', code: 'IN', filed: 1, granted: 0, pending: 1 },
  ],
  timeline: [
    { id: 'P001', title: 'Catheter Anchoring Mechanism', product: 'P01', jurisdiction: 'US', filedDate: '2021-03-15', expectedGrant: '2026-06-01', status: 'under_examination' },
    { id: 'P002', title: 'Catheter Anchoring Mechanism', product: 'P01', jurisdiction: 'EU', filedDate: '2021-06-20', expectedGrant: '2026-09-01', status: 'under_examination' },
    { id: 'P003', title: 'Self-Expanding Frame Design', product: 'P01', jurisdiction: 'US', filedDate: '2022-01-10', expectedGrant: '2027-01-01', status: 'filed' },
    { id: 'P004', title: 'Delivery Catheter Assembly', product: 'P02', jurisdiction: 'US', filedDate: '2022-08-05', expectedGrant: '2027-06-01', status: 'filed' },
    { id: 'P005', title: 'Imaging Integration Method', product: 'P03', jurisdiction: 'CA', filedDate: '2023-02-14', expectedGrant: '2028-02-01', status: 'filed' },
  ],
};

// ---------------------------------------------------------------------------
// Risks
// ---------------------------------------------------------------------------
export const RISKS = [
  { id: 'R1', title: 'Regulatory Delay', severity: 'medium', description: 'FDA PMA timeline may extend beyond Q4 2028 due to additional clinical data requirements', mitigation: 'Proactive pre-submission meetings with FDA scheduled quarterly; parallel CE marking submission in EU', owner: 'VP Regulatory', lastUpdated: '2026-02-15' },
  { id: 'R2', title: 'Key-Person Risk', severity: 'medium', description: 'Critical dependency on CTO and VP Engineering for core IP and device architecture', mitigation: 'Knowledge transfer program initiated; deputy roles established; retention packages in place', owner: 'CEO', lastUpdated: '2026-01-20' },
  { id: 'R3', title: 'Runway Risk', severity: 'low', description: 'Current runway of ~33 months provides buffer through Series B close', mitigation: 'Strict cash management; monthly board review of burn rate; bridge facility available if needed', owner: 'CFO', lastUpdated: '2026-02-28' },
  { id: 'R4', title: 'Competitive Threat', severity: 'low', description: 'Two competitors in similar clinical stage for structural heart devices', mitigation: 'Strong IP portfolio (52 patents filed); differentiated approach with catheter-based solution; first-mover advantage in key markets', owner: 'CEO', lastUpdated: '2026-02-10' },
  { id: 'R5', title: 'FX Exposure', severity: 'medium', description: 'CAD/USD fluctuations impact reported financials and US-denominated fundraise', mitigation: 'Natural hedge through US-dollar denominated capital raises; monitoring FX exposure monthly', owner: 'CFO', lastUpdated: '2026-03-01' },
];

// ---------------------------------------------------------------------------
// Commercial Pipeline & Market
// ---------------------------------------------------------------------------
export const COMMERCIAL_PIPELINE = {
  market: {
    tam: { value: 7900000000, label: 'Global structural heart devices market (2030E)', source: 'Market research, 2025' },
    sam: { value: 1950000000, label: 'Transcatheter valve repair/replacement', source: 'Industry analysis, 2025' },
    som: { value: 390000000, label: 'Addressable in initial target geographies (US, EU, CA)', source: 'Internal analysis' },
  },
  regulatoryTimeline: [
    { milestone: 'First-in-Human Study Complete', date: '2025-Q4', status: 'completed' },
    { milestone: 'CE Mark Submission', date: '2027-Q1', status: 'upcoming' },
    { milestone: 'FDA IDE Submission', date: '2026-Q4', status: 'upcoming' },
    { milestone: 'Pivotal Study Enrollment Start', date: '2027-Q2', status: 'upcoming' },
    { milestone: 'Pivotal Study Complete', date: '2028-Q2', status: 'upcoming' },
    { milestone: 'FDA PMA Submission', date: '2028-Q4', status: 'upcoming' },
    { milestone: 'Expected FDA Approval', date: '2029-Q3', status: 'upcoming' },
    { milestone: 'First Commercial Revenue (US)', date: '2029-Q4', status: 'upcoming' },
  ],
  pipeline: [
    { partner: 'Major US Hospital Network', type: 'Clinical Site Agreement', status: 'Signed', details: 'Lead pivotal study site' },
    { partner: 'European Heart Center', type: 'Clinical Site Agreement', status: 'In Discussion', details: 'CE mark clinical evidence' },
    { partner: 'Device Distributor (US)', type: 'LOI', status: 'Signed', details: 'Exclusive US distribution for initial launch' },
    { partner: 'Manufacturing CDMO', type: 'Contract', status: 'Signed', details: 'Scale-up production for pivotal quantities' },
  ],
};

// ---------------------------------------------------------------------------
// Document Vault
// ---------------------------------------------------------------------------
export const DOCUMENTS = [
  { category: 'Governance', docs: [
    { name: 'Articles of Incorporation', date: '2020-03-15', type: 'pdf' },
    { name: 'Shareholder Agreement (Amended)', date: '2025-06-20', type: 'pdf' },
    { name: 'Board Resolution - Series A.2', date: '2024-11-10', type: 'pdf' },
  ]},
  { category: 'Finance', docs: [
    { name: 'FY2025 Financial Statements (Draft)', date: '2026-02-28', type: 'xlsx' },
    { name: 'Q4 2025 Board Financial Package', date: '2025-12-31', type: 'pdf' },
    { name: 'FY2026 Budget & Forecast', date: '2025-11-15', type: 'xlsx' },
  ]},
  { category: 'Board', docs: [
    { name: 'Board Deck - February 2026', date: '2026-02-15', type: 'pptx' },
    { name: 'Board Deck - November 2025', date: '2025-11-10', type: 'pptx' },
    { name: 'Board Minutes - February 2026', date: '2026-02-20', type: 'pdf' },
  ]},
  { category: 'IP', docs: [
    { name: 'Patent Portfolio Summary', date: '2026-01-31', type: 'pdf' },
    { name: 'IP Strategy Roadmap', date: '2025-09-01', type: 'pdf' },
  ]},
  { category: 'Legal', docs: [
    { name: 'CDMO Manufacturing Agreement', date: '2025-08-15', type: 'pdf' },
    { name: 'Clinical Trial Agreements (Summary)', date: '2025-10-01', type: 'pdf' },
  ]},
];

// ---------------------------------------------------------------------------
// Alerts / Notifications
// ---------------------------------------------------------------------------
export const ALERTS = [
  { id: 'A1', type: 'milestone', title: 'First-in-Human Study completed successfully', date: '2025-12-15', link: 'roadmap' },
  { id: 'A2', type: 'board', title: 'Next Board Meeting: April 15, 2026', date: '2026-03-20', link: null },
  { id: 'A3', type: 'cap_table', title: 'Series A.2 closing finalized -- 12 investors participated', date: '2025-11-15', link: 'equity' },
  { id: 'A4', type: 'document', title: 'Q4 2025 Board Financial Package uploaded', date: '2026-01-05', link: 'documents' },
  { id: 'A5', type: 'milestone', title: 'Cleanroom facility qualification completed', date: '2025-10-30', link: 'roadmap' },
];

// ---------------------------------------------------------------------------
// Ownership / Cap Table Summary
// ---------------------------------------------------------------------------
export const OWNERSHIP_SUMMARY = {
  fullyDiluted: 12765280,
  lastRoundValuation: 138000000,
  lastRoundDate: '2024-11',
  lastRoundSeries: 'Series A.2',
  breakdown: [
    { category: 'Founders & Management', shares: 5250000, pct: 41.1 },
    { category: 'Series Seed', shares: 850000, pct: 6.7 },
    { category: 'Series A', shares: 3100000, pct: 24.3 },
    { category: 'Series A.2', shares: 1386426, pct: 10.9 },
    { category: 'Option Pool', shares: 2178854, pct: 17.1 },
  ],
};

// ---------------------------------------------------------------------------
// SPCC (Canadian Ownership Tracking)
// ---------------------------------------------------------------------------
export const SPCC_DATA = {
  canadianOwnershipPct: 58.72,
  threshold: 50,
  warningThreshold: 55,
  status: 'Active',
  lastVerified: '2026-02-28',
  srEdCreditsAtRisk: 1050000,
};
